import React from 'react';
import { FilterChip, EmptyState, Card } from '@parsina/framework';
import { CalendarClock } from 'lucide-react';

import { C, CT } from '../../../constants/theme';
import { useTranslation } from '../../../i18n/I18nProvider.jsx';
import { timelineTypeKey } from '../../../constants/vocabulary.js';
import {
  TIMELINE_TYPES, groupTimeline, filterTimeline, iconForType, targetOf, OUTSIDE_VISIT,
} from '../../../lib/timeline.js';

// خط زمانی — the record in the order it happened. core.md §7.4.
//
// The BFF derives the events and this tab only GROUPS them (src/lib/timeline.js):
// one group per visit, «خارج از ویزیت» for everything written outside one, newest
// group first. Deriving a timeline in the browser would mean the student's view
// and the assistant's prompt were assembled by two different pieces of code from
// two different readings of the same record — and the first time they disagreed,
// the model would be answering about a patient the student cannot see.
//
// Every row clicks through to where the event lives: a visit event opens the
// visit, a thread opens the conversation, a history entry opens the شرح حال tab.

export default function TimelineTab({ caseId, timeline, onNavigate }) {
  const { t, formatNumber, formatDate, formatTime } = useTranslation();
  const [types, setTypes] = React.useState([]);

  const events = React.useMemo(() => filterTimeline(timeline, types), [timeline, types]);
  const groups = React.useMemo(() => groupTimeline(events), [events]);

  const toggle = (type) => setTypes((prev) => (
    prev.includes(type) ? prev.filter((x) => x !== type) : [...prev, type]
  ));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} data-testid="record-timeline">
      <div
        style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}
        role="group"
        aria-label={t('record.timeline.filter')}
      >
        {TIMELINE_TYPES.map((type) => (
          <FilterChip
            key={type}
            label={t(timelineTypeKey(type))}
            active={types.includes(type)}
            onClick={() => toggle(type)}
            data-testid={`timeline-filter-${type}`}
          />
        ))}
      </div>

      {groups.length === 0 && (
        <EmptyState
          icon={<CalendarClock size={44} strokeWidth={1.5} aria-hidden />}
          title={t('record.timeline.empty.title')}
          description={t('record.timeline.empty.body')}
          data-testid="record-timeline-empty"
        />
      )}

      {groups.map((group) => (
        <section key={group.key} data-testid={`timeline-group-${group.key}`}>
          <h3
            style={{
              margin: '0 0 8px',
              fontSize: 12.5,
              fontWeight: 800,
              color: CT.mute,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {group.key === OUTSIDE_VISIT
              ? t('record.timeline.outside')
              : t('visit.number', { n: formatNumber(group.encounterSeq ?? 0) })}
            {group.atMs && <span>· {formatDate(group.atMs, 'numeric')}</span>}
          </h3>

          <Card style={{ padding: '4px 12px' }}>
            {group.events.map((event) => {
              const Icon = iconForType(event.type);
              const target = targetOf(event, caseId);
              return (
                <button
                  key={event.id}
                  type="button"
                  onClick={() => target && onNavigate?.(target.screen, target.data)}
                  data-testid={`timeline-event-${event.id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 9,
                    width: '100%',
                    textAlign: 'start',
                    background: 'none',
                    border: 0,
                    borderBottom: `1px solid ${C.border}`,
                    padding: '9px 0',
                    font: 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  <Icon size={16} color={C.teal} aria-hidden style={{ marginTop: 2, flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 13, color: C.textPrimary, fontWeight: 600 }}>
                      {event.title || t(timelineTypeKey(event.type))}
                    </span>
                    {event.detail && (
                      <span
                        style={{
                          display: 'block',
                          fontSize: 12,
                          color: CT.mute,
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {event.detail}
                      </span>
                    )}
                  </span>
                  {/* The clock, not the date: the date is on the group heading,
                      and repeating it on every row of a visit is noise. */}
                  <span style={{ fontSize: 11, color: CT.mute, flexShrink: 0 }}>
                    {formatTime(event.atMs)}
                  </span>
                </button>
              );
            })}
          </Card>
        </section>
      ))}
    </div>
  );
}
